export const CarWheelPositions = {
  FRONT_RIGHT: 'FRONT_RIGHT',
  FRONT_LEFT: 'FRONT_LEFT',
  REAR_RIGHT: 'REAR_RIGHT',
  REAR_LEFT: 'REAR_LEFT',
  FRONT: 'FRONT',
  REAR: 'REAR',
  MIDDLE_FRONT_RIGHT: 'MIDDLE_FRONT_RIGHT',
  MIDDLE_FRONT_LEFT: 'MIDDLE_FRONT_LEFT',
  MIDDLE_REAR_RIGHT: 'MIDDLE_REAR_RIGHT',
  MIDDLE_REAR_LEFT: 'MIDDLE_REAR_LEFT',
  FRONT_RIGHT_INNER: 'FRONT_RIGHT_INNER',
  FRONT_LEFT_INNER: 'FRONT_LEFT_INNER',
  REAR_RIGHT_INNER: 'REAR_RIGHT_INNER',
  REAR_LEFT_INNER: 'REAR_LEFT_INNER',
  MIDDLE_FRONT_RIGHT_INNER: 'MIDDLE_FRONT_RIGHT_INNER',
  MIDDLE_FRONT_LEFT_INNER: 'MIDDLE_FRONT_LEFT_INNER',
  MIDDLE_REAR_RIGHT_INNER: 'MIDDLE_REAR_RIGHT_INNER',
  MIDDLE_REAR_LEFT_INNER: 'MIDDLE_REAR_LEFT_INNER'
} as const;

export type CarWheelPositionType = (typeof CarWheelPositions)[keyof typeof CarWheelPositions];

type CarWheelRect = {
  key: CarWheelPositionType;
  x: number;
  y: number;
  width: number;
  height: number;
};

const AXLE_ALIAS: Partial<Record<CarWheelPositionType, CarWheelPositionType[]>> = {
  FRONT: [CarWheelPositions.FRONT_LEFT, CarWheelPositions.FRONT_RIGHT],
  REAR: [CarWheelPositions.REAR_LEFT, CarWheelPositions.REAR_RIGHT]
};

export const resolveFilledWheelKeys = (carSides: CarWheelPositionType[]): Set<CarWheelPositionType> => {
  const filled = new Set<CarWheelPositionType>();
  for (const side of carSides) {
    const aliases = AXLE_ALIAS[side];
    if (aliases) {
      for (const alias of aliases) {
        filled.add(alias);
      }
    } else {
      filled.add(side);
    }
  }
  return filled;
};

const isMiddle = (side: CarWheelPositionType) => side.includes('MIDDLE_');
const isInner = (side: CarWheelPositionType) => side.endsWith('_INNER');

// Classic 2-axle geometry — keep these numbers identical to the original SVG
const TWO_AXLE_OUTER: CarWheelRect[] = [
  { key: CarWheelPositions.FRONT_LEFT, x: 4, y: 6.5, width: 3, height: 8 },
  { key: CarWheelPositions.FRONT_RIGHT, x: 13, y: 6.5, width: 3, height: 8 },
  { key: CarWheelPositions.REAR_LEFT, x: 4, y: 21.5, width: 3, height: 8 },
  { key: CarWheelPositions.REAR_RIGHT, x: 13, y: 21.5, width: 3, height: 8 }
];

const TWO_AXLE_INNER: CarWheelRect[] = [
  { key: CarWheelPositions.FRONT_LEFT_INNER, x: 7.25, y: 6.5, width: 2, height: 8 },
  { key: CarWheelPositions.FRONT_RIGHT_INNER, x: 10.75, y: 6.5, width: 2, height: 8 },
  { key: CarWheelPositions.REAR_LEFT_INNER, x: 7.25, y: 21.5, width: 2, height: 8 },
  { key: CarWheelPositions.REAR_RIGHT_INNER, x: 10.75, y: 21.5, width: 2, height: 8 }
];

const FOUR_AXLE_OUTER: CarWheelRect[] = [
  { key: CarWheelPositions.FRONT_LEFT, x: 4, y: 5.5, width: 3, height: 4 },
  { key: CarWheelPositions.FRONT_RIGHT, x: 13, y: 5.5, width: 3, height: 4 },
  { key: CarWheelPositions.MIDDLE_FRONT_LEFT, x: 4, y: 11.5, width: 3, height: 4 },
  { key: CarWheelPositions.MIDDLE_FRONT_RIGHT, x: 13, y: 11.5, width: 3, height: 4 },
  { key: CarWheelPositions.MIDDLE_REAR_LEFT, x: 4, y: 17.5, width: 3, height: 4 },
  { key: CarWheelPositions.MIDDLE_REAR_RIGHT, x: 13, y: 17.5, width: 3, height: 4 },
  { key: CarWheelPositions.REAR_LEFT, x: 4, y: 23.5, width: 3, height: 4 },
  { key: CarWheelPositions.REAR_RIGHT, x: 13, y: 23.5, width: 3, height: 4 }
];

const FOUR_AXLE_INNER: CarWheelRect[] = [
  { key: CarWheelPositions.FRONT_LEFT_INNER, x: 7.25, y: 5.5, width: 2, height: 4 },
  { key: CarWheelPositions.FRONT_RIGHT_INNER, x: 10.75, y: 5.5, width: 2, height: 4 },
  { key: CarWheelPositions.MIDDLE_FRONT_LEFT_INNER, x: 7.25, y: 11.5, width: 2, height: 4 },
  { key: CarWheelPositions.MIDDLE_FRONT_RIGHT_INNER, x: 10.75, y: 11.5, width: 2, height: 4 },
  { key: CarWheelPositions.MIDDLE_REAR_LEFT_INNER, x: 7.25, y: 17.5, width: 2, height: 4 },
  { key: CarWheelPositions.MIDDLE_REAR_RIGHT_INNER, x: 10.75, y: 17.5, width: 2, height: 4 },
  { key: CarWheelPositions.REAR_LEFT_INNER, x: 7.25, y: 23.5, width: 2, height: 4 },
  { key: CarWheelPositions.REAR_RIGHT_INNER, x: 10.75, y: 23.5, width: 2, height: 4 }
];

export const getVisibleWheelRects = (carSides: CarWheelPositionType[]): CarWheelRect[] => {
  const filled = resolveFilledWheelKeys(carSides);
  const showMiddle = carSides.some(isMiddle);
  const innerKeys = new Set([...filled].filter(isInner));

  const outers = showMiddle ? FOUR_AXLE_OUTER : TWO_AXLE_OUTER;
  const inners = showMiddle ? FOUR_AXLE_INNER : TWO_AXLE_INNER;

  return [...outers, ...inners.filter((rect) => innerKeys.has(rect.key))];
};
